// ─────────────────────────────────────────────────────────────────────────────
// ActivityChart.js — the 7-day session trend in the middle card.
//
// Geometry matches the Figma frame (480×271: plot x 57→454, y 63→223, grid step
// 40) re-based to a 480×200 viewBox — the title row lives in the card header, so
// the chart only owns the plot.
//
// The viewBox width tracks the card's real pixel width (see useElementSize) so
// 1 user unit = 1 px on both axes. That fills the card without stretching:
// strokes stay even and the hover dot stays a circle — only the horizontal
// spacing between days grows on wider screens, as a real chart should.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef } from "react";
import { useElementSize } from "../../../shared/hooks";
import { monotoneXPath, areaPath, presetCeiling } from "../../../shared/lib/chartMath";
import { dayLabel, tooltipDate, humanDuration } from "../format";
import styles from "./ActivityChart.module.css";

const ACT = { H: 200, left: 57, rightPad: 26, top: 14, bottom: 174 };

// Quarter-friendly ceilings so the 5 y-ticks (max → 0) are always whole numbers
// like the design's 600/450/300/150/0.
const QUARTER_PRESETS = [4, 8, 12, 20, 40, 60, 80, 100, 200, 400, 600, 800, 1000, 2000, 4000, 6000, 10000];

/** @param {{date: string, total_sessions: number, total_duration_seconds: number}[]} historyData */
export default function ActivityChart({ historyData }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, { width }] = useElementSize({ width: 480, height: ACT.H });
  const svgRef = useRef();

  const W = width;
  const right = W - ACT.rightPad;

  const sessions = historyData.map((d) => d.total_sessions);
  const n = sessions.length;
  const maxV = presetCeiling(Math.max(...sessions, 0), QUARTER_PRESETS);

  const xs = sessions.map((_, i) => ACT.left + (n > 1 ? (i / (n - 1)) * (right - ACT.left) : 0));
  const ys = sessions.map((v) => ACT.bottom - (v / maxV) * (ACT.bottom - ACT.top));

  const line = monotoneXPath(xs, ys);
  const area = areaPath(line, xs, ACT.bottom);
  // Figma: the area gradient runs from the curve's highest point to the baseline.
  const gradTop = Math.min(Math.min(...ys), ACT.bottom - 1);

  const yTicks = [0, 1, 2, 3, 4].map((i) => ACT.top + (i / 4) * (ACT.bottom - ACT.top));

  const handleMouseMove = (e) => {
    if (n < 2) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((svgX - ACT.left) / (right - ACT.left)) * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, idx)));
  };

  const hover = hoverIdx !== null
    ? { x: xs[hoverIdx], y: ys[hoverIdx], day: historyData[hoverIdx] }
    : null;

  return (
    <div className={styles.chart} ref={wrapRef}>
      <svg
        ref={svgRef} viewBox={`0 0 ${W} ${ACT.H}`} preserveAspectRatio="none" className={styles.svg}
        onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id="actAreaGrad" x1={ACT.left} y1={gradTop} x2={ACT.left} y2={ACT.bottom} gradientUnits="userSpaceOnUse">
            <stop offset="5%"  stopColor="#00FFA3" stopOpacity="0.3" />
            <stop offset="95%" stopColor="#00FFA3" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Grid — dashed 3 3, white @ 4% */}
        {yTicks.map((y, i) => (
          <line key={`y-${i}`} x1={ACT.left} y1={y} x2={right} y2={y} stroke="white" strokeOpacity="0.04" strokeDasharray="3 3" />
        ))}
        {xs.map((x, i) => (
          <line key={`x-${i}`} x1={x} y1={ACT.top} x2={x} y2={ACT.bottom} stroke="white" strokeOpacity="0.04" strokeDasharray="3 3" />
        ))}

        {/* Y-axis tick labels — right-aligned, white @ 30% */}
        {yTicks.map((y, i) => (
          <text key={`yt-${i}`} x={ACT.left - 9} y={y + 4} textAnchor="end" fontSize="11" fill="white" fillOpacity="0.3">
            {Math.round(maxV * (1 - i / 4))}
          </text>
        ))}

        {/* X-axis day labels */}
        {historyData.map((d, i) => (
          <text key={`xt-${i}`} x={xs[i]} y={ACT.bottom + 16} textAnchor="middle" fontSize="11" fill="white" fillOpacity="0.3">
            {dayLabel(d.date)}
          </text>
        ))}

        {/* Area + dual-stroke monotone line (#10B981 w2 under #00FFA3 w1.5) */}
        {line && (
          <>
            <path d={area} fill="url(#actAreaGrad)" fillOpacity="0.6" />
            <path d={line} fill="none" stroke="#10B981" strokeWidth="2"   strokeLinecap="round" strokeLinejoin="round" />
            <path d={line} fill="none" stroke="#00FFA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )}

        {/* Hover marker — white @ 16% crosshair + black dot with a #00FFA3 ring */}
        {hover && (
          <>
            <line x1={hover.x} y1={ACT.top} x2={hover.x} y2={ACT.bottom - 1} stroke="white" strokeOpacity="0.16" />
            <circle cx={hover.x} cy={hover.y} r="5" fill="black" stroke="#00FFA3" strokeWidth="4" />
          </>
        )}
      </svg>

      {/* Tooltip — day + date on the left, labelled #00FFA3 value badges on the right */}
      {hover && (
        <div
          className={`${styles.tooltip} ${hover.x > W / 2 ? styles.tooltipFlip : ""}`}
          style={{ left: `${(hover.x / W) * 100}%`, top: `calc(${(hover.y / ACT.H) * 100}% - 17px)` }}
        >
          <div className={styles.dayCol}>
            <div className={styles.day}>{dayLabel(hover.day.date)}</div>
            <div className={styles.date}>{tooltipDate(hover.day.date)}</div>
          </div>
          <div className={styles.badgeCol}>
            <div className={styles.badgeLabel}>Sessions</div>
            <div className={styles.badge}>{hover.day.total_sessions.toLocaleString()}</div>
          </div>
          <div className={styles.badgeCol}>
            <div className={styles.badgeLabel}>Duration</div>
            <div className={styles.badge}>{humanDuration(hover.day.total_duration_seconds)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
